import { Lock, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { guestAssetUrl } from '../apiBaseUrl';
import { AuthedImage } from '../ui';
import { preloadGuestImage } from '../ui/guestImageCache';
import type { ArrivalStep } from '../useGuestSession';

interface Props {
  step: ArrivalStep;
  /** Ohne Nummer stehen die Karten gleichrangig nebeneinander (Ausweichmöglichkeiten). */
  number?: number;
  /** Ersatz, wenn der Eintrag kein Foto hat. */
  icon: LucideIcon;
  token: string | undefined;
  onOpen: () => void;
}

/** Ein Eintrag als Karte zum Wischen: Foto, Nummer, Titel und der Anfang des Texts. */
export function ArrivalStepCard({ step, number, icon: Icon, token, onOpen }: Props) {
  const { t } = useTranslation();
  const image = step.images?.[0];
  // Mit Maus oder Tastatur schon beim Zielen das große Bild holen: bis das Fenster offen ist, ist
  // es meist da. Nicht beim Berühren — jedes Wischen durch die Kartenreihe begänne mit einem
  // Download, am Handy genau dort, wo das Netz am knappsten ist.
  const warmUp = () => {
    if (image) preloadGuestImage(guestAssetUrl(image.urls.full), token);
  };
  return (
    <button
      type="button"
      onClick={onOpen}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse') warmUp();
      }}
      onFocus={warmUp}
      className="bg-bg-elev border-line rounded-guest-card shadow-guest-card hover:border-accent/40 flex h-full w-full flex-col overflow-hidden border text-left transition-colors"
    >
      {image ? (
        <AuthedImage
          url={guestAssetUrl(image.urls.thumb)}
          token={token}
          alt=""
          className="h-33 w-full object-cover"
        />
      ) : (
        <span className="bg-bg-soft text-fg-dim h-33 flex w-full items-center justify-center">
          <Icon className="size-6" aria-hidden />
        </span>
      )}
      <span className="flex gap-2.5 p-3.5">
        {number !== undefined && (
          <span
            className={cn(
              'flex size-[26px] shrink-0 items-center justify-center rounded-full text-xs font-bold',
              number === 1 ? 'bg-accent text-white' : 'bg-bg-soft text-fg',
            )}
          >
            {number}
          </span>
        )}
        <span className="flex min-w-0 flex-col gap-1">
          <span className="text-fg line-clamp-2 text-sm font-semibold leading-snug">
            {step.title}
          </span>
          {step.body && <span className="text-fg-dim line-clamp-2 text-xs">{step.body}</span>}
          {/* Gesperrt heißt: der Code im Text ist noch verdeckt — oder der ganze Text fehlt. */}
          {step.isLocked && (
            <span className="text-fg-dim flex items-center gap-1 text-xs">
              <Lock className="size-3" aria-hidden />
              {t('travel.arrival.lockedShort')}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
